<template>
    <VJoyPanel v-bind="{}" :data-testid="dataTestid" class="profile__resume-import__experience" title-size="small">
        <template #panel-title>
            <h2 :data-testid="`${dataTestid}--company`">{{ experience.company }}</h2>
        </template>
        <template #panel-subtitle>
            <h3 :data-testid="`${dataTestid}--title`">{{ experience.title }}</h3>
            <ExperienceItemDateRange
                v-if="experience.startDate"
                :start-date="experience.startDate"
                :end-date="experience.endDate"
                :current="experience.currentJob || false"
                :data-testid="`${dataTestid}--date-range`"
            />
            <ExperienceItemLocation :location="experience.location?.formattedAddress" :data-testid="`${dataTestid}--location`" />
        </template>
        <template #panel-title-action>
            <VJoyWrapper :no-margin="true" column-gap="2">
                <ResumeImportExperienceSourceComponent
                    v-if="experience.source"
                    :experience-id="experience.id"
                    :experience-source="experience.source"
                    :data-testid="`${dataTestid}-source`"
                />
                <VJoyIconButton
                    v-if="props.isVisibleCtaEdit"
                    icon="pen"
                    variant="ghost"
                    size="xsmall"
                    :data-testid="`${dataTestid}--cta-edit`"
                    :label="$t('general.edit')"
                    @click="handleClickOnCtaEdit"
                />
                <VJoyIconButton
                    icon="trash"
                    variant="ghost"
                    size="xsmall"
                    :data-testid="`${dataTestid}--cta-delete`"
                    :disabled="!isEnabledCtaDelete"
                    :label="$t('general.delete')"
                    @click="handleClickOnCtaDelete"
                />
            </VJoyWrapper>
        </template>
        <template #panel-body>
            <ExperienceItemDescription :description="experience.description" :data-testid="`${dataTestid}--description`" />
            <VJoyHighlight
                v-if="props.isVisibleValidationWarning && (invalidCompanyOrJobTitleWarning || invalidDateOrDescriptionWarning)"
                level="warning"
                :data-testid="`${dataTestid}--validation-warning`"
                class="profile__resume-import__experience__validation-warning"
            >
                <div>
                    <span>{{ invalidCompanyOrJobTitleWarning || invalidDateOrDescriptionWarning }}</span>
                    <VJoyLink
                        href="#"
                        :text="$t('profile.experience.funnel.step.experience_list.experience.warning.click_to_add')"
                        @click="handleClickOnCtaEdit"
                    />
                </div>
            </VJoyHighlight>
        </template>
        <template v-if="hasRecommendation" #panel-action>
            <VJoyLink
                v-if="hasRecommendation"
                :href="`#${anchorsPageProfile.recommendationsSection}`"
                target="_blank"
                :data-testid="`${dataTestid}--recommendations-link`"
            >
                {{
                    $t('profile.experience.funnel.step.experience_list.recommendations.related', {nbReco: props.experience.recommendationCount || 0})
                }}
            </VJoyLink>
        </template>
    </VJoyPanel>
</template>
<script setup lang="ts">
    import {computed, toRef} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyIconButton, VJoyHighlight, VJoyLink, VJoyPanel, VJoyWrapper} from '@maltjoy/core-vue';
    import ExperienceItemDateRange from '~/components/_common/experience/ExperienceItemDateRange.vue';
    import ExperienceItemDescription from '~/components/_common/experience/ExperienceItemDescription.vue';
    import ExperienceItemLocation from '~/components/_common/experience/ExperienceItemLocation.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {type ResumeImportExperience} from '~/features/resume-import/types/ResumeImportExperience';
    import ResumeImportExperienceSourceComponent from '~/features/resume-import/ui/ResumeImportExperienceSource.vue';
    import {useRulesStore} from '~/store/rules/rulesStore';

    const {t} = useTranslation();
    const rulesStore = useRulesStore();

    const props = withDefaults(
        defineProps<{
            experience: ResumeImportExperience;
            isEnabledCtaDelete?: boolean;
            isVisibleCtaEdit?: boolean;
            isVisibleValidationWarning?: boolean;
        }>(),
        {
            isEnabledCtaDelete: true,
            isVisibleCtaEdit: false,
            isVisibleValidationWarning: false,
        },
    );

    const emit = defineEmits<{
        'resume-import-experience:delete': [value: ResumeImportExperience];
        'resume-import-experience:edit': [value: ResumeImportExperience];
    }>();

    const isEnabledCtaDelete = toRef(props, 'isEnabledCtaDelete');
    const dataTestid = computed(() => `resume-experience-item--${props.experience.id}`);
    const hasRecommendation = computed(() => !!props.experience.recommendationCount && props.experience.recommendationCount > 0);
    const invalidCompanyOrJobTitleWarning = computed(() => {
        if (!props.experience.title || !props.experience.company) {
            return t('profile.experience.funnel.step.experience_list.items.generic.warning');
        }
        return undefined;
    });
    const invalidDateOrDescriptionWarning = computed(() => {
        const hasInvalidDate = !rulesStore.isValidExperienceDateRange(
            props.experience.startDate,
            props.experience.endDate,
            props.experience.currentJob,
        );
        const hasInvalidDescription = !rulesStore.validateExperienceDescription(props.experience.description);

        if (hasInvalidDate && hasInvalidDescription) {
            return t('profile.experience.funnel.step.experience_list.items.specific.warning.date.and.description');
        }
        if (hasInvalidDate) {
            return t('profile.experience.funnel.step.experience_list.items.specific.warning.date');
        }
        if (hasInvalidDescription) {
            return t('profile.experience.funnel.step.experience_list.items.specific.warning.description');
        }
        return undefined;
    });

    function handleClickOnCtaDelete() {
        emit('resume-import-experience:delete', props.experience);
    }

    function handleClickOnCtaEdit() {
        emit('resume-import-experience:edit', props.experience);
    }
</script>
<style lang="scss">
    .profile__resume-import__experience {
        &.joy-panel {
            padding: var(--joy-core-spacing-4);
            width: 100%;
            max-width: 100%;

            .joy-panel__header-subtitle {
                h3 {
                    font-family: var(--joy-font-family-base);
                    font-weight: var(--joy-font-weight-bold);
                    font-size: var(--joy-font-size-primary-400);
                    line-height: var(--joy-line-height-large);
                }
            }

            .joy-panel__body {
                padding-bottom: var(--joy-core-spacing-2);
            }

            .joy-panel__footer {
                border-top: 1px solid var(--joy-color-neutral-30);
                padding-top: var(--joy-core-spacing-3);

                a {
                    padding: 0;
                    margin: 0;
                    font-weight: var(--joy-font-weight-normal);
                }
            }
        }

        &__source {
            display: flex;
            align-items: center;
            gap: 0.3em;

            &.experience-source-malt {
                &__link {
                    &__link {
                        color: var(--joy-color-primary-50) !important;
                        font-weight: var(--joy-font-weight-normal);
                    }
                }

                &.experience-source-resume {
                    color: var(--joy-color-secondary-50);
                }
            }
        }
    }
</style>
